import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shirt, Home, Camera, Bug, Clock, Sparkles, User, LogOut, Users, MessageCircle, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout, isAdmin } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Главная', icon: Home },
    { path: '/clothes', label: 'Одежда', icon: Shirt },
    { path: '/try-on', label: 'Примерка', icon: Camera },
    { path: '/openrouter-try-on', label: 'OpenRouter', icon: Sparkles },
    { path: '/chat', label: 'Чат', icon: MessageCircle },
    { path: '/history', label: 'История', icon: Clock },
    { path: '/debug', label: 'Debug', icon: Bug },
  ];

  // Добавляем админские маршруты
  if (isAuthenticated && isAdmin()) {
    navItems.push(
      { path: '/accounts', label: 'Аккаунты', icon: Users }
    );
  }

  // Закрываем мобильное меню при переходе на другую страницу
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2 shrink-0">
            <Shirt className="h-8 w-8 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">Viton</h1>
          </div>

          {/* Навигация - на широких экранах в строку, на узких скрыта за кнопкой-гамбургером */}
          <nav className="hidden lg:flex lg:items-center lg:space-x-1 xl:space-x-2">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === path
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            ))}
          </nav>

          {/* User Section - только на широких экранах, на узких переезжает в мобильное меню */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700 max-w-[12rem] truncate">{user?.email}</span>
                  {isAdmin() && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                      Admin
                    </span>
                  )}
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>Профиль</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Выйти</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors"
              >
                <User className="h-4 w-4" />
                <span>Войти</span>
              </Link>
            )}
          </div>

          {/* Кнопка мобильного меню */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Мобильное меню */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <nav className="px-4 py-3 space-y-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === path
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            ))}
          </nav>

          <div className="px-4 py-3 border-t border-gray-200 space-y-1">
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-2 px-3 py-2">
                  <User className="h-4 w-4 text-gray-600 shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{user?.email}</span>
                  {isAdmin() && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full shrink-0">
                      Admin
                    </span>
                  )}
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>Профиль</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Выйти</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors"
              >
                <User className="h-4 w-4" />
                <span>Войти</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
